import React, { useState, useEffect } from "react";
import { Container, Card, Button, Row, Col, Badge, Form, Alert, Spinner, ProgressBar } from "react-bootstrap";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { db, callFunction, callXeroFunction } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";

export default function SyncXeroPage() {
  const { user } = useAuth();
  const { language } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [appClients, setAppClients] = useState([]);
  const [xeroClients, setXeroClients] = useState([]);
  const [matches, setMatches] = useState([]);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState({});
  const [syncedCount, setSyncedCount] = useState(0);
  const [searchTerms, setSearchTerms] = useState({});

  // Carregar clientes do app
  useEffect(() => {
    if (!user?.isAdmin) return;
    loadAppClients();
  }, [user]);

  const loadAppClients = async () => {
    try {
      const snap = await getDocs(collection(db, "clients"));
      const clients = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAppClients(clients);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      setLoading(false);
    }
  };

  // Conectar ao Xero e buscar clientes
  const connectToXero = async () => {
    setLoading(true);
    try {
      const backendUrl = window.location.hostname === 'localhost'
        ? 'http://127.0.0.1:5001/fechamento-semanal/us-central1'
        : 'https://api.aitomize.space';

      const authPath = window.location.hostname === 'localhost'
        ? '/xeroAuthInit'
        : '/xero/auth/init';

      const authWindow = window.open(
        `${backendUrl}${authPath}`,
        'XeroAuth',
        'width=600,height=700'
      );

      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await callXeroFunction('xeroAuthStatusV1');

          if (statusResponse.authenticated) {
            clearInterval(pollInterval);
            if (authWindow && !authWindow.closed) {
              authWindow.close();
            }

            console.log('Fetching Xero clients...');
            const clientsResponse = await callXeroFunction('xeroGetClients');

            const xeroContacts = clientsResponse.clients;
            console.log(`Received ${xeroContacts.length} Xero clients`);

            setXeroClients(xeroContacts);
            setXeroConnected(true);
            findMatches(appClients, xeroContacts);
            setLoading(false);
          }
        } catch (pollError) {
          // Ignore polling errors
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(pollInterval);
        if (authWindow && !authWindow.closed) {
          authWindow.close();
        }
        setLoading(false);
      }, 300000);

    } catch (error) {
      console.error("Erro ao conectar ao Xero:", error);
      alert(`${t(language, "syncErrorConnect")} ${error.message}`);
      setLoading(false);
    }
  };

  // Encontrar correspondências entre clientes
  const findMatches = (appClients, xeroClients) => {
    const matchesList = appClients.map(appClient => {
      const appName = appClient.name.toLowerCase().trim();

      const potentialMatches = xeroClients.filter(xeroClient => {
        const xeroName = xeroClient.name.toLowerCase().trim();
        if (appName === xeroName) return true;
        const appFirstWord = appName.split(' ')[0];
        const xeroFirstWord = xeroName.split(' ')[0];
        if (appFirstWord === xeroFirstWord && appFirstWord.length >= 3) return true;
        if (xeroName.includes(appName) && appName.length >= 3) return true;
        if (appName.includes(xeroName) && xeroName.length >= 3) return true;
        return false;
      });

      potentialMatches.sort((a, b) => {
        const simA = calculateSimilarity(appName, a.name.toLowerCase());
        const simB = calculateSimilarity(appName, b.name.toLowerCase());
        return simB - simA;
      });

      return {
        appClient,
        potentialMatches,
        status: appClient.xeroContactId ? 'synced' : 'pending'
      };
    });

    setMatches(matchesList);
  };

  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    if (longer.length === 0) return 1.0;
    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const getEditDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) matrix[i] = [i];
    for (let j = 0; j <= str1.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  const handleSelectMatch = (appClientId, xeroClient) => {
    setSelectedMatches({
      ...selectedMatches,
      [appClientId]: xeroClient
    });
  };

  const syncClient = async (appClient, xeroClient) => {
    if (!xeroClient) return;

    setSyncing(true);
    try {
      console.log('Xero Client Data:', xeroClient);

      const invoiceResponse = await callXeroFunction('xeroGetInvoiceData', { contactId: xeroClient.contactID });
      const { invoiceAmount, invoiceDescription } = invoiceResponse;

      let phone = "";
      if (xeroClient.phones && xeroClient.phones.length > 0) {
        for (const phoneObj of xeroClient.phones) {
          const phoneNumber = phoneObj.phoneNumber || phoneObj.PhoneNumber || "";
          if (phoneNumber && phoneNumber.trim() !== "") {
            phone = phoneNumber.trim();
            break;
          }
        }
      }

      if (!phone && appClient.phone) {
        phone = appClient.phone;
      }

      const updateData = {
        xeroContactId: xeroClient.contactID,
        email: xeroClient.emailAddress || appClient.email || "",
        phone: phone,
        paymentMethod: "xero",
        defaultInvoiceAmount: invoiceAmount || 0,
        invoiceDescription: invoiceDescription || ""
      };

      await updateDoc(doc(db, "clients", appClient.id), updateData);

      setAppClients(prev =>
        prev.map(c => c.id === appClient.id ? { ...c, ...updateData } : c)
      );

      setMatches(prev =>
        prev.map(m =>
          m.appClient.id === appClient.id
            ? { ...m, status: 'synced' }
            : m
        )
      );

      setSyncedCount(prev => prev + 1);

      const newSelected = { ...selectedMatches };
      delete newSelected[appClient.id];
      setSelectedMatches(newSelected);

    } catch (error) {
      console.error("Erro ao sincronizar cliente:", error);

      if (error.response?.status === 401 || error.response?.status === 500) {
        alert(
          `${t(language, "syncSessionExpired")}\n\n` +
          t(language, "syncTokenTip")
        );
      } else {
        alert(`${t(language, "syncErrorSync")} ${error.message}`);
      }
    }
    setSyncing(false);
  };

  const getFilteredXeroMatches = (appClientId, potentialMatches) => {
    const searchTerm = searchTerms[appClientId] || "";
    if (!searchTerm) return potentialMatches;

    const term = searchTerm.toLowerCase();
    return xeroClients.filter(xeroClient => {
      const xeroName = xeroClient.name?.toLowerCase() || "";
      const xeroEmail = xeroClient.emailAddress?.toLowerCase() || "";
      const xeroPhone = xeroClient.phones?.[0]?.phoneNumber?.toLowerCase() || "";
      const xeroAddress = xeroClient.addresses?.[0]?.addressLine1?.toLowerCase() || "";

      return xeroName.includes(term) ||
             xeroEmail.includes(term) ||
             xeroPhone.includes(term) ||
             xeroAddress.includes(term);
    });
  };

  if (!user?.isAdmin) {
    return (
      <Container className="py-5 text-center">
        <h5>{t(language, "syncAccessDenied")}</h5>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">{t(language, "syncLoading")}</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="mb-4">
        <h2>{t(language, "syncTitle")}</h2>
        <p className="text-muted">
          {t(language, "syncDesc")}
        </p>
      </div>

      {!xeroConnected ? (
        <Card className="text-center p-5 shadow-sm">
          <div className="mb-4">
            <div style={{ fontSize: '4rem' }}>🔐</div>
            <h3 className="mt-3">{t(language, "syncConnect")}</h3>
            <p className="text-muted">
              {t(language, "syncConnectDesc")}
            </p>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={connectToXero}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t(language, "syncConnecting")}
              </>
            ) : (
              <>{t(language, "syncConnect")}</>
            )}
          </Button>
        </Card>
      ) : (
        <>
          {/* Progress Bar */}
          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{t(language, "syncProgress")}</strong>
                <span>{syncedCount} / {matches.filter(m => m.status === 'pending').length + syncedCount}</span>
              </div>
              <ProgressBar
                now={(syncedCount / appClients.length) * 100}
                label={`${Math.round((syncedCount / appClients.length) * 100)}%`}
              />
            </Card.Body>
          </Card>

          {/* Alert sobre Token Expirado */}
          <Alert variant="warning" className="mb-4 d-flex justify-content-between flex-wrap gap-2 align-items-center">
            <div>
              <strong>{t(language, "syncTip")}</strong> {t(language, "syncTokenTip")}
            </div>
            <Button
              variant="warning"
              size="sm"
              onClick={() => {
                const backendUrl = window.location.hostname === 'localhost'
                  ? 'http://127.0.0.1:5001/fechamento-semanal/us-central1'
                  : 'https://api.aitomize.space';
                const authPath = window.location.hostname === 'localhost'
                  ? '/xeroAuthInit'
                  : '/xero/auth/init';
                window.open(`${backendUrl}${authPath}`, '_blank', 'width=800,height=600');
              }}
            >
              {t(language, "syncReconnect")}
            </Button>
          </Alert>

          {/* Alert de Informação */}
          <Alert variant="info" className="mb-4">
            <strong>{t(language, "syncHowItWorks")}</strong>
            <ul className="mb-0 mt-2">
              <li>{t(language, "syncHowStep1")}</li>
              <li>{t(language, "syncHowStep2")}</li>
              <li>{t(language, "syncHowStep3")}</li>
              <li>{t(language, "syncHowStep4")}</li>
              <li>{t(language, "syncHowStep5")}</li>
            </ul>
          </Alert>

          {/* Lista de Matches */}
          <div className="d-flex flex-column gap-3">
            {matches.map((match, index) => (
              <Card key={match.appClient.id} className={`shadow-sm ${match.status === 'synced' ? 'border-success' : ''}`}>
                <Card.Body>
                  <Row className="align-items-center">
                    {/* Cliente do App */}
                    <Col md={4}>
                      <div className="d-flex align-items-start gap-2">
                        <div style={{ fontSize: '2rem' }}>👤</div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <strong>{match.appClient.name}</strong>
                            {match.status === 'synced' && (
                              <Badge bg="success">✓ {t(language, "syncSynced")}</Badge>
                            )}
                          </div>
                          <div className="text-muted small">
                            <div>{match.appClient.address}</div>
                            {match.appClient.email && <div>{match.appClient.email}</div>}
                            {match.appClient.phone && <div>{match.appClient.phone}</div>}
                          </div>
                        </div>
                      </div>
                    </Col>

                    {/* Seta */}
                    <Col md={1} className="text-center d-none d-md-block">
                      <div style={{ fontSize: '2rem' }}>→</div>
                    </Col>

                    {/* Opções do Xero */}
                    <Col md={7}>
                      {match.status === 'synced' ? (
                        <div>
                          <Alert variant="success" className="mb-2 d-flex justify-content-between align-items-center">
                            <span>{t(language, "syncAlreadySynced")}</span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                const xeroClient = xeroClients.find(xc => xc.contactID === match.appClient.xeroContactId);
                                if (xeroClient) {
                                  syncClient(match.appClient, xeroClient);
                                } else {
                                  alert(t(language, "syncXeroContactNotFound"));
                                }
                              }}
                              disabled={syncing}
                            >
                              {t(language, "syncUpdateData")}
                            </Button>
                          </Alert>
                          {match.appClient.xeroContactId && (
                            <div className="text-muted small">
                              <strong>Xero Contact ID:</strong> {match.appClient.xeroContactId}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small className="text-muted">
                              {searchTerms[match.appClient.id] ? (
                                <>
                                  {getFilteredXeroMatches(match.appClient.id, match.potentialMatches).length} {t(language, "syncResultsFound").replace("{total}", xeroClients.length)}
                                </>
                              ) : (
                                <>
                                  {match.potentialMatches.length === 0 ? (
                                    <span className="text-warning">{t(language, "syncNoSuggestion")}</span>
                                  ) : (
                                    <>{match.potentialMatches.length} {t(language, "syncSuggestions")}</>
                                  )}
                                </>
                              )}
                            </small>
                          </div>

                          <Form.Group className="mb-3">
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder={t(language, "syncSearchPlaceholder")}
                              value={searchTerms[match.appClient.id] || ""}
                              onChange={(e) => setSearchTerms({
                                ...searchTerms,
                                [match.appClient.id]: e.target.value
                              })}
                            />
                          </Form.Group>

                          {getFilteredXeroMatches(match.appClient.id, match.potentialMatches).length === 0 ? (
                            <Alert variant="info" className="mb-0">
                              <div className="d-flex align-items-center gap-2">
                                <div>
                                  {searchTerms[match.appClient.id] ? (
                                    <>{t(language, "syncNoResults").replace("{term}", searchTerms[match.appClient.id])}</>
                                  ) : (
                                    <>{t(language, "syncSearchHint")}</>
                                  )}
                                </div>
                              </div>
                            </Alert>
                          ) : (
                          <div className="d-flex flex-column gap-2">
                            {getFilteredXeroMatches(match.appClient.id, match.potentialMatches).map((xeroClient, idx) => (
                              <div
                                key={xeroClient.contactID}
                                className={`p-3 rounded border ${
                                  selectedMatches[match.appClient.id]?.contactID === xeroClient.contactID
                                    ? 'border-primary bg-primary bg-opacity-10'
                                    : 'border-secondary'
                                }`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSelectMatch(match.appClient.id, xeroClient)}
                              >
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <Form.Check
                                        type="radio"
                                        checked={selectedMatches[match.appClient.id]?.contactID === xeroClient.contactID}
                                        onChange={() => handleSelectMatch(match.appClient.id, xeroClient)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <strong>{xeroClient.name}</strong>
                                      {idx === 0 && (
                                        <Badge bg="info">{t(language, "syncBestMatch")}</Badge>
                                      )}
                                    </div>
                                    <div className="text-muted small ms-4">
                                      {xeroClient.emailAddress && (
                                        <div>{xeroClient.emailAddress}</div>
                                      )}
                                      {xeroClient.phones?.[0]?.phoneNumber && (
                                        <div>{xeroClient.phones[0].phoneNumber}</div>
                                      )}
                                      {xeroClient.addresses?.[0] && (
                                        <div>{xeroClient.addresses[0].addressLine1}, {xeroClient.addresses[0].city}</div>
                                      )}
                                    </div>
                                  </div>
                                  {selectedMatches[match.appClient.id]?.contactID === xeroClient.contactID && (
                                    <Button
                                      size="sm"
                                      variant="success"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        syncClient(match.appClient, xeroClient);
                                      }}
                                      disabled={syncing}
                                    >
                                      {syncing ? (
                                        <Spinner animation="border" size="sm" />
                                      ) : (
                                        `✓ ${t(language, "syncSyncBtn")}`
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </div>

          {/* Botão de Sincronizar Todos */}
          {matches.filter(m => m.status === 'pending').length > 0 && (
            <Card className="mt-4 text-center p-4 shadow-sm">
              <p className="text-muted mb-3">
                {t(language, "syncSureMatches")}
              </p>
              <Button
                variant="primary"
                size="lg"
                disabled={Object.keys(selectedMatches).length === 0}
              >
                {t(language, "syncSyncAllSelected")} ({Object.keys(selectedMatches).length})
              </Button>
            </Card>
          )}
        </>
      )}
    </Container>
  );
}
